// file: `react/src/ReactApp.tsx`:

import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import FarfarNavbar from "./components/FarfarNavbar/FarfarNavbar.tsx";
import MessageThread from './pages/MessageThread.tsx';
import Devops from './pages/Devops/Devops.tsx';
import Join from './pages/Join/Join.tsx';
import Login from './pages/Login/Login.tsx';
import Signup from './pages/Signup/Signup.tsx';
import Logout from './components/Logout.tsx';
import ShowMD from './pages/ShowMD.tsx';
import UserThreads from './pages/UserThreads.tsx';
import Settings from './pages/Settings/Settings.tsx';
import Recovery from './pages/Recovery/RecoveryPage.tsx';
import AccessPage from './pages/Access/AccessPage.tsx';
import AccessSessionShell from './pages/Access/AccessSessionShell.tsx';
import BuildStamp from './components/BuildStamp.tsx';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword.tsx';
import ResetPassword from './pages/ResetPassword/ResetPassword.tsx';
import Account from './pages/Account/Account.tsx';
import SchoolPromptsPage from './school/SchoolPromptsPage.tsx';
import SchoolCasesPage from './school/SchoolCasesPage.tsx';
import SchoolReviewPage from './school/SchoolReviewPage.tsx';
import { AUTH_TOKEN_CHANGED_EVENT, hasSession, markCookieSession } from './utils/authToken.ts';
import { expressURL } from './utils/networkUtils';

const PUBLIC_PATHS = [
  '/login',
  '/devops',
  '/show-md',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/logout'
];

function isPublicPath(pathname: string) {
  if (PUBLIC_PATHS.includes(pathname)) return true;
  return pathname.startsWith('/join/');
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sessionReady, setSessionReady] = React.useState(() => hasSession());

  React.useEffect(() => {
    if (hasSession()) {
      setSessionReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${expressURL()}/auth/me`, { credentials: 'include' });
        if (!cancelled && response.ok) {
          markCookieSession(true);
        }
      } catch (_) {
        /* signed out */
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const redirectIfUnauthenticated = React.useCallback(() => {
    if (!sessionReady) return;
    if (!hasSession() && !isPublicPath(location.pathname)) {
      navigate('/login');
    }
  }, [location.pathname, navigate, sessionReady]);

  React.useEffect(() => {
    redirectIfUnauthenticated();
  }, [redirectIfUnauthenticated]);

  React.useEffect(() => {
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, redirectIfUnauthenticated);
    return () => window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, redirectIfUnauthenticated);
  }, [redirectIfUnauthenticated]);

  return (
    <div className='routes-container'>
      {/* {location.pathname !== '/login' && <FarfarNavbar />} */}
      {location.pathname.startsWith('/access/session/') ? null : <FarfarNavbar />}
      <BuildStamp />
      <Routes>
        <Route path="/" element={<MessageThread />} />
        <Route path="/m/:threadId" element={<MessageThread />} />
        <Route path="/threads" element={<Navigate to="/" replace />} />
        <Route path="/devops" element={<Devops />} />
        <Route path="/show-md" element={<ShowMD />} />
        <Route path="/join/:token" element={<Join />} />
        <Route path="/login/*" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/user-threads" element={<UserThreads />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/recovery" element={<Recovery />} />
        <Route path="/access/session/:sessionId" element={<AccessSessionShell />} />
        <Route path="/access" element={<AccessPage />} />
        <Route path="/screening" element={<Navigate to="/recovery" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account" element={<Account />} />
        <Route path="/school/prompts" element={<SchoolPromptsPage />} />
        <Route path="/school/cases" element={<SchoolCasesPage />} />
        <Route path="/school/review" element={<SchoolReviewPage />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <div className='full-page'>
      <Router>
        <AppContent />
      </Router>
    </div>
  );
}

export default App;
